<template>
    <div class="row m-3">
        <!-- Vue Loading -->
        <loading :active.sync="loading"
                 :is-full-page="true"></loading>

        <div class="col-12 mb-2">
            <h1>
                Channel Settings <small>- {{ $store.getters.getActiveChannelName || 'No Title' }}</small>
            </h1>
        </div>

        <!-- Validation -->
        <div class="col-12" v-if="errors != null">
            <div class="alert alert-danger">
                <strong>{{ $t('CHANNEL_SETTINGS_VALIDATION_ERRORS') }}:</strong>
                <ul>
                    <li v-for="error in errors">{{ error.path }}: {{ $t(getKeyByErrorCode(error.errorCode)) }}</li>
                </ul>
            </div>
        </div>

        <div class="col-12 col-xl-6" v-if="settings">

            <!-- Default Language Settings -->
            <div class="row mb-2">
                <div class="col">
                    <DefaultLanguageSettings></DefaultLanguageSettings>
                </div>
            </div>

            <!-- Default Import Visibility Settings -->
            <div class="row mb-2">
                <div class="col">
                    <DefaultImportVisibility></DefaultImportVisibility>
                </div>
            </div>

            <!-- Shared Channel Settings -->
            <div class="row mb-2" v-if="$store.getters.isLoggedIn && $store.state.user.isAdmin">
                <div class="col">
                    <SharedChannelSettings></SharedChannelSettings>
                </div>
            </div>

            <!-- Category Settings -->
            <div class="row mb-2">
                <div class="col">
                    <CategorySettings></CategorySettings>
                </div>
            </div>

            <!-- Device Settings -->
            <div class="row mb-2" v-if="$store.getters.isLoggedIn && $store.state.user.isAdmin">
                <div class="col">
                    <DeviceSettings></DeviceSettings>
                </div>
            </div>
        </div>

        <!-- Access Token to copy -->
        <div class="col-12 col-xl-6" v-if="settings">
            <div class="row mb-2" v-if="$store.getters.isLoggedIn && $store.state.user.isAdmin">
                <div class="col">

                    <div class="card bg-light mb-3">
                        <div class="card-header">
                            <h5 class="card-title">
                                {{ $t('CHANNEL_ACCESS_TOKEN') }}
                            </h5>
                        </div>

                        <div class="card-body">
                            <input type="text" class="form-control disabled" id="channelAccessToken"
                                   v-model="channelAccessToken" disabled="disabled">

                            <button class="btn btn-primary mt-2"
                                    v-clipboard:copy="channelAccessToken"
                                    v-clipboard:success="accessTokenCopySuccess"
                                    v-clipboard:error="accessTokenCopyError">
                                <i class="fa fa-clipboard"></i>&nbsp;
                                {{ $t('CHANNEL_ACCESS_TOKEN_CLICK_TO_COPY') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Default Members -->
            <div class="row mb-2">
                <div class="col">
                    <DefaultMembers></DefaultMembers>
                </div>
            </div>

            <!-- Shared Channel - Instance Move -->
            <div class="row mb-2" v-if="settings.isShared">
                <div class="col">
                    <SharedChannelMoveInstanceSettings/>
                </div>
            </div>

            <!-- Stream Settings -->
            <div class="row mb-2" v-if="$store.getters.isLoggedIn && $store.state.user.isAdmin">
                <div class="col">
                    <StreamSettings></StreamSettings>
                </div>
            </div>
        </div>

        <div class="col-12 mt-3">
            <button class="btn btn-primary mt-2" @click="saveSettings">
                {{ $t('UI_MODAL_BUTTON_SAVE') }}
            </button>
            <button class="btn btn-dark mt-2 ml-1" @click="resetSettingsToDefault" v-if="$store.getters.isLoggedIn && $store.state.user.isAdmin">
                {{ $t('UI_MODAL_BUTTON_RESET') }}
            </button>
        </div>

    </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import StreamSettings from './Settings/StreamSettings';
import DefaultMembers from './DefaultMembers/DefaultMembers';

import { RepositoryFactory } from '../../../../repositories/RepositoryFactory';

import DefaultLanguageSettings from './Settings/DefaultLanguageSettings';
import DeviceSettings from './Settings/DeviceSettings';
import CategorySettings from './Settings/CategorySettings';
import SharedChannelSettings from './Settings/SharedChannelSettings';
import DefaultImportVisibility from './Settings/DefaultImportVisibility';
import SharedChannelMoveInstanceSettings from './Settings/SharedChannelMoveInstanceSettings';

const ChannelSettingsRepository = RepositoryFactory.get('channelSettings');


export default {
  name: 'ChannelSettingsOverview',
  components: {
    SharedChannelMoveInstanceSettings,
    DefaultImportVisibility,
    SharedChannelSettings,
    CategorySettings,
    DeviceSettings,
    DefaultLanguageSettings,
    Loading,
    DefaultMembers,
    StreamSettings,
  },
  data() {
    return {
      channelAccessToken: '',
      loading: true,
      errors: null,
    };
  },
  async mounted() {
    await ChannelSettingsRepository.fetchSettings()
      .then((res) => {
        this.loading = false;

        // If there is no default language, set the current i18 selected Language as default
        if (res.data.settings.languageCode === undefined) {
          res.data.settings.languageCode = this.$i18n.locale;
        }
        this.channelAccessToken = res.data.access_token;
      });
  },
  methods: {
    accessTokenCopySuccess(e) {
      this.$snotify.info(this.$t('CHANNEL_ACCESS_TOKEN_COPY_SUCCESS'));
    },
    accessTokenCopyError(e) {
      this.$snotify.error(this.$t('CHANNEL_ACCESS_TOKEN_COPY_ERROR'));
    },
    /**
			 * Saves the settings.
			 *
			 * @param stream
			 */
    async saveSettings() {
      this.errors = null;
      this.loading = true;

      await ChannelSettingsRepository.saveSettings({ settings: this.settings })
        .then((res) => {
          this.$snotify.info('Settings saved');
        })
        .catch((error) => {
          if (error.response.data.error === this.getErrorCodeByKey('ERROR_PARAMETER_INVALID')) {
            this.errors = error.response.data.errors;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
			 * Resets all settings to the default.
			 *
			 * @returns {Promise<boolean>}
			 */
    async resetSettingsToDefault() {
      if (!this.$store.getters.isLoggedIn || !this.$store.state.user.isAdmin) return false;

      this.loading = true;
      await ChannelSettingsRepository.resetSettings()
        .then(() => {
          ChannelSettingsRepository.fetchSettings()
            .then((res) => {
              this.loading = false;
              this.$store.commit('setChannelSettings', res.data.settings);
              this.$snotify.info('Settings have been set to default.');
            });
        });
    },
  },
  computed: {
    settings() {
      return this.$store.getters.getChannelSettings;
    },
  },
};
</script>

<style scoped>

</style>
